/**
 * The four independent language axes.
 *
 * Conflating these is the most common mistake in this area. A Spanish-speaking coordinator may plan
 * an English Mass, export a Spanish worksheet, and send notifications in each recipient's own
 * language — all four differing, legitimately, in one workflow.
 *
 * See docs/engineering/12-localization.md
 */

/** Both first-class at launch. Spanish is not a later translation. */
export const LAUNCH_LANGUAGES = ['en', 'es'] as const;
export type LaunchLanguage = (typeof LAUNCH_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: LaunchLanguage = 'en';

export function isLaunchLanguage(value: string): value is LaunchLanguage {
  return (LAUNCH_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Negotiate a launch language from what a client asks for.
 *
 * **Most people reading this application will never have an Account.** A family opening a
 * Participant Link has no stored preference and never will, so the browser's language list is not a
 * fallback here — it is the primary signal, and on the participant surface it is the only one.
 * Somebody whose phone is in Spanish should meet a Spanish page without being asked.
 *
 * Shared rather than frontend-only because the backend negotiates the same thing when resolving a
 * recipient's notification language from what is known about them.
 *
 * Order: an explicit choice, then what was asked for, then English.
 *
 * @param chosen  a language the person selected, if any. Untrusted — it may come from local storage.
 * @param accepted BCP-47 tags in preference order (`navigator.languages`, or `Accept-Language`).
 */
export function negotiateLanguage(
  chosen: string | null | undefined,
  accepted: readonly string[],
): LaunchLanguage {
  if (chosen && isLaunchLanguage(chosen)) return chosen;

  for (const candidate of accepted) {
    // `es-MX`, `es-419` and `ES` are all Spanish. Matching the base subtag case-insensitively is
    // what makes that true without enumerating every region — and `es-419` is exactly the tag a
    // Latin American device sends, which is most of the Spanish-speaking parishes this serves.
    const base = candidate.trim().toLowerCase().split('-')[0];
    if (base && isLaunchLanguage(base)) return base;
  }

  return DEFAULT_LANGUAGE;
}

/**
 * The axes themselves. Named so that a function signature cannot silently accept the wrong one.
 */
export const LANGUAGE_AXES = [
  /** Application chrome, chosen by the viewer. */
  'interface',
  /** The language the liturgy is celebrated in — a planning value, not a UI setting. */
  'liturgical',
  /** The rendered document, chosen per export. */
  'export',
  /** Each email, resolved per recipient. */
  'notification',
] as const;

export type LanguageAxis = (typeof LANGUAGE_AXES)[number];

/**
 * Interface fallback chain. A missing key renders the English string and logs a warning — it never
 * renders the raw key.
 *
 * Template *content* falls back differently: to the Template's own primary language, which may not
 * be English. That resolution lives in the templates context.
 */
export const INTERFACE_FALLBACK_CHAIN: readonly LaunchLanguage[] = ['en'];

export function resolveInterfaceLanguage(requested: string | undefined): LaunchLanguage {
  if (requested && isLaunchLanguage(requested)) return requested;
  return DEFAULT_LANGUAGE;
}
